import { type FormEvent, useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { returnCasesHttp } from '@/services/returnCases'
import type {
  BrokerImportRow,
  BrokerImportRowPage,
  CapitalResourceResponse,
  CapitalWorkspace,
} from '@/types/returnCase.types'

export interface CapitalGainsPanelClient {
  capitalWorkspace: (caseId: string) => Promise<CapitalWorkspace>
  brokerImportRows: (
    caseId: string,
    batchId: string,
    page?: number,
    pageSize?: number
  ) => Promise<BrokerImportRowPage>
  flagBrokerImportRow: (
    caseId: string,
    rowId: string,
    input: Record<string, unknown>
  ) => Promise<CapitalResourceResponse>
  validateBrokerImport?: (
    caseId: string,
    input: Record<string, unknown>
  ) => Promise<CapitalResourceResponse>
}

interface Props {
  caseId: string
  canImport: boolean
  canFlag: boolean
  sourceType?: 'CUSTOMER_MANUAL' | 'STAFF_MANUAL'
  onChanged?: () => void | Promise<void>
  client?: CapitalGainsPanelClient
}

const field = (form: FormData, key: string) => String(form.get(key) ?? '').trim()

export default function CapitalGainsPanel({
  caseId,
  canImport,
  canFlag,
  sourceType = 'STAFF_MANUAL',
  onChanged,
  client = returnCasesHttp,
}: Props) {
  const queryClient = useQueryClient()
  const [batchId, setBatchId] = useState('')
  const [page, setPage] = useState(1)
  const [selectedRow, setSelectedRow] = useState<BrokerImportRow | null>(null)
  const [error, setError] = useState('')
  const workspace = useQuery({
    queryKey: ['capital-gains', caseId],
    queryFn: () => client.capitalWorkspace(caseId),
  })
  useEffect(() => {
    if (!batchId && workspace.data?.broker_imports[0]?.id) {
      setBatchId(workspace.data.broker_imports[0].id)
    }
  }, [batchId, workspace.data])
  const rows = useQuery({
    queryKey: ['capital-gains-broker-rows', caseId, batchId, page],
    queryFn: () => client.brokerImportRows(caseId, batchId, page, 100),
    enabled: Boolean(batchId),
  })
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['capital-gains', caseId] }),
      queryClient.invalidateQueries({ queryKey: ['capital-gains-broker-rows', caseId] }),
    ])
    await onChanged?.()
  }
  const importBatch = useMutation({
    mutationFn: (form: HTMLFormElement) => {
      if (!workspace.data || !client.validateBrokerImport) {
        throw new Error('Broker import is not available in this session')
      }
      const data = new FormData(form)
      const parsed = JSON.parse(field(data, 'rows_json')) as unknown
      if (!Array.isArray(parsed)) throw new Error('Rows JSON must be an array')
      return client.validateBrokerImport(caseId, {
        expected_revision: workspace.data.case_revision,
        source_type: sourceType,
        source_reference: field(data, 'source_reference'),
        reason: field(data, 'reason'),
        schema_version: 'broker-import-v1',
        source_period: field(data, 'source_period'),
        document_version_id: field(data, 'document_version_id'),
        rows: parsed,
      })
    },
    onSuccess: async result => {
      setError('')
      setBatchId(result.id)
      setPage(1)
      await refresh()
    },
    onError: cause =>
      setError(cause instanceof Error ? cause.message : 'Broker import was rejected'),
  })
  const flagRow = useMutation({
    mutationFn: ({ kind, narrative }: { kind: string; narrative: string }) => {
      if (!workspace.data || !selectedRow) throw new Error('Select an imported row')
      return client.flagBrokerImportRow(caseId, selectedRow.id, {
        expected_revision: workspace.data.case_revision,
        source_type: sourceType,
        source_reference: `broker-row:${selectedRow.id}`,
        reason: 'Imported capital row was reviewed',
        flag_kind: kind,
        narrative,
      })
    },
    onSuccess: async () => {
      setError('')
      await refresh()
    },
    onError: cause => setError(cause instanceof Error ? cause.message : 'Row flag was rejected'),
  })

  if (workspace.isLoading) return <p>Loading capital-gains ledger…</p>
  if (workspace.isError || !workspace.data) {
    return (
      <div className="alert alert-error" role="alert">
        Capital-gains ledger is unavailable.
      </div>
    )
  }
  const ledger = workspace.data
  const totalPages = rows.data ? Math.max(1, Math.ceil(rows.data.total / rows.data.page_size)) : 1

  return (
    <section
      className="mt-6 rounded border border-border p-4"
      aria-label="Capital gains, VDA and loss ledgers"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold">Capital gains, VDA and loss ledgers</h2>
          <p className="text-sm text-text-secondary">
            Lot-level classifications and import mappings are review records. Imported rows never
            change accepted tax facts automatically.
          </p>
        </div>
        <span className="badge">Revision {ledger.case_revision}</span>
      </div>
      {error && (
        <div className="alert alert-error mt-3" role="alert">
          {error}
        </div>
      )}

      <div className="mt-4 grid gap-3 sm:grid-cols-4" aria-label="Capital ledger totals">
        <Metric label="Lots" value={ledger.lots.length} />
        <Metric label="Transfers" value={ledger.transfers.length} />
        <Metric label="VDA transactions" value={ledger.vda_transactions.length} />
        <Metric label="Loss balances" value={ledger.loss_entries.length} />
      </div>

      <div className="mt-5 overflow-x-auto">
        <table className="w-full text-left text-sm">
          <caption className="mb-2 text-left font-medium">
            Lot classifications and explanations
          </caption>
          <thead>
            <tr>
              <th>Transfer</th>
              <th>Lot</th>
              <th>Term</th>
              <th>Rate bucket</th>
              <th>Gain/loss</th>
              <th>Rule and explanation</th>
            </tr>
          </thead>
          <tbody>
            {ledger.classifications.map(item => (
              <tr key={item.id} className="border-t border-border align-top">
                <td className="py-2 font-mono text-xs">{item.transfer_id.slice(0, 8)}</td>
                <td className="py-2 font-mono text-xs">{item.lot_id.slice(0, 8)}</td>
                <td>{item.term}</td>
                <td>{item.rate_bucket}</td>
                <td>{item.gain_or_loss}</td>
                <td>
                  <details>
                    <summary>Explain</summary>
                    <pre className="max-w-xl whitespace-pre-wrap text-xs">
                      {JSON.stringify(
                        { rules: item.rule_keys, lines: item.explanation_lines },
                        null,
                        2
                      )}
                    </pre>
                  </details>
                </td>
              </tr>
            ))}
            {ledger.classifications.length === 0 && (
              <tr>
                <td colSpan={6} className="py-3 text-text-secondary">
                  No accepted transfer classifications.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-6 border-t border-border pt-4">
        <h3 className="font-medium">Broker and AIS candidate review</h3>
        <p className="text-sm text-text-secondary">
          “Mapped” means the source row matched the versioned import shape; it does not mean an
          accepted lot or transfer was created.
        </p>
        {ledger.broker_imports.length > 0 && (
          <label className="mt-3 block max-w-md text-sm">
            Import batch
            <select
              className="input mt-1 w-full"
              value={batchId}
              onChange={event => {
                setBatchId(event.target.value)
                setPage(1)
                setSelectedRow(null)
              }}
            >
              {ledger.broker_imports.map(batch => (
                <option key={batch.id} value={batch.id}>
                  {batch.schema_version} · {batch.row_count} rows · {batch.status}
                </option>
              ))}
            </select>
          </label>
        )}
        {rows.isError && (
          <div className="alert alert-error mt-3" role="alert">
            Imported rows could not be loaded.
          </div>
        )}
        {rows.data && (
          <>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-left text-sm">
                <caption className="mb-2 text-left">
                  Page {page} of {totalPages}; {rows.data.total} retained source rows
                </caption>
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Kind</th>
                    <th>Instrument</th>
                    <th>Amount</th>
                    <th>Disposition</th>
                    <th>Review</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.data.items.map(item => (
                    <tr key={item.id} className="border-t border-border">
                      <td>{item.source_ordinal}</td>
                      <td>{item.row_kind}</td>
                      <td>{String(item.normalized_row.asset_identifier ?? '')}</td>
                      <td>{String(item.normalized_row.amount ?? '')}</td>
                      <td>
                        <span className="badge">{item.disposition}</span>
                        {item.flags.length > 0 && (
                          <span className="ml-1 text-xs">{item.flags.length} flag(s)</span>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setSelectedRow(item)}
                        >
                          Review row {item.source_ordinal}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                className="btn btn-secondary"
                disabled={page <= 1}
                onClick={() => setPage(value => value - 1)}
              >
                Previous
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={page >= totalPages}
                onClick={() => setPage(value => value + 1)}
              >
                Next
              </button>
            </div>
          </>
        )}
        {canFlag && selectedRow && (
          <FlagForm
            row={selectedRow}
            pending={flagRow.isPending}
            onSubmit={(kind, narrative) => flagRow.mutate({ kind, narrative })}
          />
        )}
      </div>

      {canImport && client.validateBrokerImport && (
        <form
          className="mt-6 border-t border-border pt-4"
          onSubmit={(event: FormEvent<HTMLFormElement>) => {
            event.preventDefault()
            importBatch.mutate(event.currentTarget)
          }}
        >
          <h3 className="font-medium">Validate a versioned broker envelope</h3>
          <p className="text-sm text-text-secondary">
            Maximum 10,000 rows. Evidence must already be an active case document version.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input name="document_version_id" label="Document version ID" />
            <Input name="source_period" label="Source period" placeholder="2025-26" />
            <Input name="source_reference" label="Source reference" />
            <Input name="reason" label="Review reason" />
          </div>
          <label className="mt-3 block text-sm">
            Rows JSON
            <textarea
              className="input mt-1 min-h-36 w-full font-mono text-xs"
              name="rows_json"
              required
              placeholder='[{"source_ordinal":1,"row_kind":"TRANSFER","account_reference":"****1234","asset_identifier":"INE...","transaction_date":"2025-08-01","quantity":"1","amount":"100"}]'
            />
          </label>
          <button className="btn btn-primary mt-3" type="submit" disabled={importBatch.isPending}>
            Validate without accepting facts
          </button>
        </form>
      )}
    </section>
  )
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded border border-border p-3">
      <div className="text-xs text-text-secondary">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  )
}

function Input({
  name,
  label,
  placeholder,
}: {
  name: string
  label: string
  placeholder?: string
}) {
  return (
    <label className="mt-3 block text-sm">
      {label}
      <input className="input mt-1 w-full" name={name} required placeholder={placeholder} />
    </label>
  )
}

function FlagForm({
  row,
  pending,
  onSubmit,
}: {
  row: BrokerImportRow
  pending: boolean
  onSubmit: (kind: string, narrative: string) => void
}) {
  return (
    <form
      className="mt-4 rounded border border-border p-4"
      aria-label={`Flag imported row ${row.source_ordinal}`}
      onSubmit={event => {
        event.preventDefault()
        const data = new FormData(event.currentTarget)
        onSubmit(field(data, 'flag_kind'), field(data, 'narrative'))
      }}
    >
      <h4 className="font-medium">Flag row {row.source_ordinal} for professional review</h4>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="mt-3 block text-sm">
          Issue
          <select className="input mt-1 w-full" name="flag_kind" required>
            <option value="MISSING">Missing item</option>
            <option value="DUPLICATE">Duplicate</option>
            <option value="INCORRECT">Incorrect mapping or amount</option>
          </select>
        </label>
        <Input name="narrative" label="What needs correction" />
      </div>
      <button className="btn btn-secondary mt-3" type="submit" disabled={pending}>
        Record flag; do not alter accepted facts
      </button>
    </form>
  )
}
